import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/feature/PageSEO';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials, milestones } from '@/mocks/testimonials';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;

    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('submitting');

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          urlEncoded.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9qqf1fnahb7382ovdl0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          '提交失败，请稍后再试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <PageSEO
        title="关于我们"
        description="国产自拍完全指南 — 最专业的国产手机自拍技巧教学平台。了解我们的品牌故事、发展历程、核心价值观，以及联系方式。"
        keywords="关于自拍指南,自拍教程品牌,摄影教学平台"
        canonicalUrl="https://www.selfie-guide.cn/about"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于我们 - 国产自拍完全指南',
          description: '最专业的国产手机自拍技巧教学平台',
          url: 'https://www.selfie-guide.cn/about',
        }}
      />
      <Navbar />

      <main className="pt-20 min-h-screen">
        {/* Page Header */}
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-4">
              关于我们
            </h1>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-50" id="brand">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                  让每个人都能拍出好看的自拍
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    自拍指南诞生于一个简单的想法：为什么朋友用同一款国产手机拍照，效果差距这么大？
                    我们三位创始人都是摄影爱好者，在日常交流中发现，大多数人并非设备不行，而是缺少正确的拍摄方法指导。
                  </p>
                  <p>
                    2023年，我们决定创建这个平台，专注于国产手机自拍技巧的教学与分享。
                    从手机选购、构图法则、光线运用到后期修图，我们构建了一套完整的自拍教学体系。
                    每一篇教程都由经验丰富的摄影师和博主共同创作，确保内容既专业又易懂。
                  </p>
                  <p>
                    我们相信，<strong>好的自拍不是靠昂贵的设备，而是靠正确的方法和持续的练习</strong>。
                    无论你使用的是哪款国产手机，只要掌握核心技巧，都能拍出让人眼前一亮的高质感大片。
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-8">
                  <div className="p-4 bg-background-100 rounded-xl text-center">
                    <div className="text-2xl font-bold text-primary-600 font-heading">100万+</div>
                    <div className="text-xs text-foreground-600 mt-1">月活跃读者</div>
                  </div>
                  <div className="p-4 bg-background-100 rounded-xl text-center">
                    <div className="text-2xl font-bold text-accent-600 font-heading">50+</div>
                    <div className="text-xs text-foreground-600 mt-1">深度教程</div>
                  </div>
                  <div className="p-4 bg-background-100 rounded-xl text-center">
                    <div className="text-2xl font-bold text-primary-600 font-heading">6</div>
                    <div className="text-xs text-foreground-600 mt-1">学习板块</div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Creative%20photography%20studio%20workspace%20with%20warm%20lighting%2C%20modern%20smartphone%20on%20wooden%20table%2C%20camera%20equipment%20and%20notebook%2C%20minimalist%20aesthetic%2C%20cream%20walls%2C%20inspirational%20creative%20atmosphere%2C%20soft%20natural%20light%20from%20window&width=600&height=500&seq=about-brand-photography&orientation=squarish"
                  alt="自拍指南工作室"
                  className="w-full rounded-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-100" id="milestones">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((m, idx) => (
                  <div
                    key={idx}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="hidden md:block pl-12 md:pl-0">
                        <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold mb-2">
                          {m.year}
                        </span>
                        <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">
                          {m.title}
                        </h3>
                        <p className="text-sm text-foreground-600">{m.description}</p>
                      </div>
                    </div>
                    <div className="absolute left-2 md:left-1/2 w-5 h-5 bg-accent-500 rounded-full border-4 border-background-100 -translate-x-1/2 z-10"></div>
                    <div className="flex-1 pl-10 md:hidden">
                      <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold mb-2">
                        {m.year}
                      </span>
                      <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">
                        {m.title}
                      </h3>
                      <p className="text-sm text-foreground-600">{m.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials / Reputation */}
        <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-50" id="testimonials">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10">
              读者评价
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="p-6 bg-background-50 border border-background-200/70 rounded-xl"
                >
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-950">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-100" id="contact">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-8">
              有任何问题或建议？欢迎给我们留言，我们会在48小时内回复
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入留言主题"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
              </div>

              {/* Anti-spam honeypot */}
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    {formError}
                  </p>
                </div>
              )}

              {formStatus === 'success' && (
                <div className="p-3 bg-accent-100/70 border border-accent-300 rounded-lg">
                  <p className="text-sm text-accent-800 flex items-center gap-2">
                    <i className="ri-check-line"></i>
                    您的留言已成功提交！我们会尽快与您联系。
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'submitting' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中...
                  </span>
                ) : (
                  '提交留言'
                )}
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.selfie-guide.cn/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          }),
        }}
      />
    </>
  );
}